import React, { useEffect, useMemo, useState } from 'react';
import './ClosingStockCheck.css';

/**
 * ClosingStockAttestation — spec C6-4, the mandatory step before /apply.
 *
 * A compact version of ClosingStockCheck's three checks, rendered as a modal so
 * it sits in front of the Consolidated Sheet on EVERY commit path (Verify or
 * Move to Consolidate alike):
 *   1. quantities & prices confirmed and signed by the Owner AND the Store Manager;
 *   2. saved preparations checked in detail (Yes / No + remarks);
 *   3. when the count was taken (before sale / after closing time).
 *
 * onConfirm receives { owner, store_manager, preps_checked, preps_remarks,
 * timing, signed_at } and the page stores it on the count it certifies.
 */
export default function ClosingStockAttestation({ isOpen, onCancel, onConfirm }) {
  const [owner, setOwner] = useState('');
  const [manager, setManager] = useState('');
  const [prepsChecked, setPrepsChecked] = useState(null);   // true | false | null
  const [remarks, setRemarks] = useState('');
  const [timing, setTiming] = useState(null);               // 'before_sale' | 'after_close' | null

  // Reset when the modal CLOSES, not when it opens: the open flips from an async
  // handler, and a reset-on-open effect can flush after the first keystroke and
  // wipe it.
  useEffect(() => {
    if (isOpen) return;
    setOwner(''); setManager(''); setPrepsChecked(null); setRemarks(''); setTiming(null);
  }, [isOpen]);

  const complete = useMemo(
    () => owner.trim().length > 0 && manager.trim().length > 0 && prepsChecked !== null && timing !== null,
    [owner, manager, prepsChecked, timing]
  );

  if (!isOpen) return null;

  const confirm = () => {
    if (!complete) return;
    onConfirm({
      owner: owner.trim(),
      store_manager: manager.trim(),
      preps_checked: prepsChecked === true,
      preps_remarks: remarks.trim(),
      timing,
      signed_at: new Date().toISOString()
    });
  };

  return (
    <div className="cs-modal-overlay" onClick={onCancel}>
      <div className="cs-modal cs-attestation-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="cs-modal-title">Closing Stock Check</div>
        <div className="cs-modal-subtitle">
          Final confirmation &middot; values are saved only after all three checks pass.
        </div>

        <section className="csc-card">
          <div className="csc-card__title"><span className="csc-card__num">1</span>Quantity &amp; price confirmation</div>
          <p className="csc-card__help">Every quantity and price must be confirmed and signed by BOTH the Owner and the Store Manager.</p>
          <input
            type="text"
            className="csc-input"
            placeholder="Owner — type full name to sign"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
          />
          <input
            type="text"
            className="csc-input"
            style={{ marginTop: 8 }}
            placeholder="Store Manager — type full name to sign"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
          />
        </section>

        <section className="csc-card">
          <div className="csc-card__title"><span className="csc-card__num">2</span>Saved preparations &mdash; detailed check</div>
          <p className="csc-card__help">Have all saved preparations been checked in detail?</p>
          <div className="csc-seg" role="group" aria-label="Preparations checked">
            {[['Yes', true], ['No', false]].map(([label, val]) => (
              <button
                key={label}
                type="button"
                className={`csc-seg-btn ${prepsChecked === val ? 'is-active' : ''}`}
                aria-pressed={prepsChecked === val}
                onClick={() => setPrepsChecked(val)}
              >
                {label}
              </button>
            ))}
          </div>
          {prepsChecked === false && (
            <div className="csc-note csc-note--danger" role="alert">
              Preparations not fully checked &mdash; review saved preparations before saving, or closing stock may be inaccurate.
            </div>
          )}
          <textarea
            className="csc-input"
            style={{ marginTop: 8 }}
            placeholder="Remarks — what was checked in the preparations (optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </section>

        <section className="csc-card">
          <div className="csc-card__title"><span className="csc-card__num">3</span>Closing stock timing</div>
          <p className="csc-card__help">When was this closing stock taken? Only the TSR and closing stock of that day are kept, based on this.</p>
          <div className="csc-seg" role="group" aria-label="Closing stock timing">
            {[['Before sale', 'before_sale'], ['After closing time', 'after_close']].map(([label, val]) => (
              <button
                key={val}
                type="button"
                className={`csc-seg-btn ${timing === val ? 'is-active' : ''}`}
                aria-pressed={timing === val}
                onClick={() => setTiming(val)}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        <div className="cs-modal-actions">
          <button type="button" className="cs-modal-cancel" onClick={onCancel}>Back</button>
          <button
            type="button"
            className="cs-modal-confirm"
            onClick={confirm}
            disabled={!complete}
            title={complete ? 'Confirm and save closing stock values to the app' : 'Complete all 3 checks to save'}
          >
            Confirm &amp; Save to App
          </button>
        </div>
      </div>
    </div>
  );
}
